// @ts-nocheck
import React, { useState } from 'react';
import { useAppState, useDispatch } from '../store';
import * as api from '../api';
import type { Folder, Board } from '../types';
import ShareBoard from './ShareBoard';

export default function Sidebar() {
  const state = useAppState();
  const dispatch = useDispatch();
  const [addingBoard, setAddingBoard] = useState(false);
  const [addingBoardFolderId, setAddingBoardFolderId] = useState<string | null>(null);
  const [newBoardName, setNewBoardName] = useState('');
  const [addingFolder, setAddingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMsg, setInviteMsg] = useState('');
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editFolderName, setEditFolderName] = useState('');
  const [editingBoardId, setEditingBoardId] = useState<string | null>(null);
  const [editBoardName, setEditBoardName] = useState('');
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);
  const [folderCtxMenu, setFolderCtxMenu] = useState<{ id: string; x: number; y: number } | null>(null);
  const [boardCtxMenu, setBoardCtxMenu] = useState<{ board: Board; x: number; y: number } | null>(null);
  const [moveToMenu, setMoveToMenu] = useState<string | null>(null); // boardId for move-to submenu
  const [showArchived, setShowArchived] = useState(false);
  const [shareBoardId, setShareBoardId] = useState<string | null>(null);

  const folders = [...state.folders].sort((a, b) => a.position - b.position);
  const activeBoards = state.boards.filter((b) => !b.is_archived);
  const archivedBoards = state.boards.filter((b) => b.is_archived);
  const favoriteBoards = activeBoards.filter((b) => b.is_favorite);
  const rootBoards = activeBoards.filter((b) => !b.folder_id);
  const boardsInFolder = (folderId: string) => activeBoards.filter((b) => b.folder_id === folderId);

  const toggleFolder = (id: string) => {
    const next = new Set(expandedFolders);
    next.has(id) ? next.delete(id) : next.add(id);
    setExpandedFolders(next);
  };

  const handleCreateBoard = async (folderId: string | null) => {
    if (!newBoardName.trim() || !state.activeWorkspaceId) return;
    const board = await api.createBoard(state.activeWorkspaceId, newBoardName.trim(), folderId || undefined);
    dispatch({ type: 'ADD_BOARD', board: { ...board, folder_id: folderId, is_private: 1, is_favorite: 0, is_archived: 0, board_type: 'board' } as any });
    setNewBoardName('');
    setAddingBoard(false);
    setAddingBoardFolderId(null);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || !state.activeWorkspaceId) return;
    const folder = await api.createFolder(state.activeWorkspaceId, newFolderName.trim());
    dispatch({ type: 'ADD_FOLDER', folder: folder as Folder });
    setExpandedFolders((prev) => new Set(prev).add(folder.id));
    setNewFolderName('');
    setAddingFolder(false);
  };

  const handleRenameFolder = async (id: string) => {
    if (!editFolderName.trim()) { setEditingFolderId(null); return; }
    await api.updateFolder(id, { name: editFolderName.trim() });
    dispatch({ type: 'UPDATE_FOLDER', id, data: { name: editFolderName.trim() } });
    setEditingFolderId(null);
  };

  const handleDeleteFolder = async (id: string) => {
    if (!confirm('Delete this folder? Boards will be moved to root.')) return;
    await api.deleteFolder(id);
    dispatch({ type: 'REMOVE_FOLDER', id });
  };

  const handleSelectBoard = async (boardId: string) => {
    dispatch({ type: 'SET_ACTIVE_BOARD', id: boardId });
    dispatch({ type: 'SET_LOADING', loading: true });
    try {
      const data = await api.getBoardData(boardId);
      dispatch({ type: 'SET_BOARD_DATA', groups: data.groups, columns: data.columns, items: data.items, cells: data.cells });
      dispatch({ type: 'SET_BOARD_ROLE', role: (data as any).boardRole || null });
    } catch (e) {
      console.error('Failed to load board:', e);
    }
    dispatch({ type: 'SET_LOADING', loading: false });
  };

  const handleWorkspaceChange = async (wsId: string) => {
    dispatch({ type: 'SET_ACTIVE_WORKSPACE', id: wsId });
    dispatch({ type: 'SET_ACTIVE_BOARD', id: null });
    const [{ boards }, { folders: flds }] = await Promise.all([
      api.getBoards(wsId),
      api.getFolders(wsId),
    ]);
    dispatch({ type: 'SET_BOARDS', boards });
    dispatch({ type: 'SET_FOLDERS', folders: flds });
  };

  // Board context menu actions
  const handleRenameBoard = (board: Board) => {
    setEditingBoardId(board.id);
    setEditBoardName(board.name);
    setBoardCtxMenu(null);
  };

  const handleRenameBoardSave = async (id: string) => {
    if (!editBoardName.trim()) { setEditingBoardId(null); return; }
    await api.updateBoard(id, { name: editBoardName.trim() } as any);
    dispatch({ type: 'UPDATE_BOARD', id, data: { name: editBoardName.trim() } });
    setEditingBoardId(null);
  };

  const handleDeleteBoard = async (board: Board) => {
    if (!confirm(`Delete board "${board.name}"? This cannot be undone.`)) return;
    await api.deleteBoard(board.id);
    dispatch({ type: 'REMOVE_BOARD', id: board.id });
    if (state.activeBoardId === board.id) {
      dispatch({ type: 'SET_ACTIVE_BOARD', id: null });
    }
    setBoardCtxMenu(null);
  };

  const handleDuplicateBoard = async (board: Board) => {
    const dup = await api.duplicateBoard(board.id);
    dispatch({ type: 'ADD_BOARD', board: dup });
    setBoardCtxMenu(null);
  };

  const handleToggleFavorite = async (board: Board) => {
    const val = board.is_favorite ? 0 : 1;
    await api.updateBoard(board.id, { is_favorite: val } as any);
    dispatch({ type: 'UPDATE_BOARD', id: board.id, data: { is_favorite: val } });
    setBoardCtxMenu(null);
  };

  const handleArchiveBoard = async (board: Board) => {
    const val = board.is_archived ? 0 : 1;
    await api.updateBoard(board.id, { is_archived: val } as any);
    dispatch({ type: 'UPDATE_BOARD', id: board.id, data: { is_archived: val } });
    if (val && state.activeBoardId === board.id) {
      dispatch({ type: 'SET_ACTIVE_BOARD', id: null });
    }
    setBoardCtxMenu(null);
  };

  const handleMoveBoard = async (boardId: string, folderId: string | null) => {
    await api.updateBoard(boardId, { folder_id: folderId } as any);
    dispatch({ type: 'UPDATE_BOARD', id: boardId, data: { folder_id: folderId } });
    setBoardCtxMenu(null);
    setMoveToMenu(null);
  };

  const handleChangeBoardType = async (board: Board) => {
    const types = ['board', 'dashboard', 'doc'];
    const current = types.indexOf(board.board_type || 'board');
    const next = types[(current + 1) % types.length];
    await api.updateBoard(board.id, { board_type: next } as any);
    dispatch({ type: 'UPDATE_BOARD', id: board.id, data: { board_type: next } });
    setBoardCtxMenu(null);
  };

  const handleOpenNewTab = (board: Board) => {
    window.open(window.location.origin + '/?board=' + board.id, '_blank');
    setBoardCtxMenu(null);
  };

  // Drag board to folder
  const handleBoardDragStart = (e: React.DragEvent, boardId: string) => {
    e.dataTransfer.setData('boardId', boardId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleFolderDrop = async (e: React.DragEvent, folderId: string | null) => {
    e.preventDefault();
    setDragOverFolderId(null);
    const boardId = e.dataTransfer.getData('boardId');
    if (!boardId) return;
    await api.updateBoard(boardId, { folder_id: folderId } as any);
    dispatch({ type: 'UPDATE_BOARD', id: boardId, data: { folder_id: folderId } });
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim() || !state.activeWorkspaceId) return;
    try {
      await api.inviteMember(state.activeWorkspaceId, inviteEmail.trim());
      setInviteMsg('Invitation sent!');
      setInviteEmail('');
      setTimeout(() => setInviteMsg(''), 3000);
    } catch (e: any) {
      setInviteMsg(e.message || 'Failed to invite');
    }
  };

  const boardIcon = (board: Board) => {
    if (board.board_type === 'dashboard') return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>;
    if (board.board_type === 'doc') return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>;
    if (board.is_private) return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;
    return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>;
  };

  const renderBoardItem = (board: Board) => (
    <div
      key={board.id}
      className={`sidebar-item ${state.activeBoardId === board.id ? 'active' : ''}`}
      onClick={() => handleSelectBoard(board.id)}
      onContextMenu={(e) => { e.preventDefault(); setBoardCtxMenu({ board, x: e.clientX, y: e.clientY }); }}
      draggable
      onDragStart={(e) => handleBoardDragStart(e, board.id)}
    >
      <span className="sidebar-item-icon" style={{ display: 'flex', alignItems: 'center' }}>{boardIcon(board)}</span>
      {editingBoardId === board.id ? (
        <input
          autoFocus
          value={editBoardName}
          onChange={(e) => setEditBoardName(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') handleRenameBoardSave(board.id);
            if (e.key === 'Escape') setEditingBoardId(null);
          }}
          onBlur={() => handleRenameBoardSave(board.id)}
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'white', border: '1px solid #d0d4e4', color: '#323338',
            outline: 'none', borderRadius: 4, padding: '2px 6px', fontSize: 13, width: '100%',
          }}
        />
      ) : (
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {board.name}
          {board.is_favorite ? <svg width="10" height="10" viewBox="0 0 24 24" fill="#f5a623" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg> : null}
        </span>
      )}
    </div>
  );

  const renderAddBoard = (folderId: string | null) => {
    const isAdding = addingBoard && addingBoardFolderId === folderId;
    if (isAdding) {
      return (
        <div style={{ padding: '4px 16px 4px 32px' }}>
          <input
            autoFocus
            value={newBoardName}
            onChange={(e) => setNewBoardName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateBoard(folderId);
              if (e.key === 'Escape') { setAddingBoard(false); setNewBoardName(''); }
            }}
            onBlur={() => { if (!newBoardName.trim()) { setAddingBoard(false); setNewBoardName(''); } }}
            placeholder="Board name..."
            style={{
              width: '100%', background: 'white', border: '1px solid #d0d4e4',
              borderRadius: 6, padding: '6px 8px', color: '#323338', outline: 'none', fontSize: 13,
            }}
          />
        </div>
      );
    }
    return (
      <button
        className="sidebar-add-btn"
        style={{ paddingLeft: folderId ? 32 : 16 }}
        onClick={() => { setAddingBoard(true); setAddingBoardFolderId(folderId); }}
      >
        <span>+</span> Add Board
      </button>
    );
  };

  if (state.sidebarCollapsed) {
    return (
      <div className="sidebar collapsed">
        <div className="sidebar-header">
          <button className="sidebar-toggle" onClick={() => dispatch({ type: 'TOGGLE_SIDEBAR' })}>▶</button>
        </div>
      </div>
    );
  }

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <h2>WorkBoard</h2>
        <button className="sidebar-toggle" onClick={() => dispatch({ type: 'TOGGLE_SIDEBAR' })}>◀</button>
      </div>

      {state.workspaces.length > 1 && (
        <div className="sidebar-ws-selector">
          <select value={state.activeWorkspaceId || ''} onChange={(e) => handleWorkspaceChange(e.target.value)}>
            {state.workspaces.map((ws) => (
              <option key={ws.id} value={ws.id}>{ws.name}</option>
            ))}
          </select>
        </div>
      )}

      <div className="sidebar-section" style={{ flex: 1, overflowY: 'auto' }}>
        {/* Favorites */}
        {favoriteBoards.length > 0 && (
          <>
            <div className="sidebar-section-title">Favorites</div>
            {favoriteBoards.map(renderBoardItem)}
          </>
        )}

        <div className="sidebar-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Boards</span>
          <button onClick={() => setAddingFolder(true)} style={{ color: '#9699a6', fontSize: 16, padding: '0 4px' }} title="New folder">
            + Folder
          </button>
        </div>

        {addingFolder && (
          <div style={{ padding: '4px 16px' }}>
            <input
              autoFocus
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateFolder();
                if (e.key === 'Escape') { setAddingFolder(false); setNewFolderName(''); }
              }}
              onBlur={() => { if (!newFolderName.trim()) { setAddingFolder(false); setNewFolderName(''); } }}
              placeholder="Folder name..."
              style={{
                width: '100%', background: 'white', border: '1px solid #d0d4e4',
                borderRadius: 6, padding: '6px 8px', color: '#323338', outline: 'none', fontSize: 13,
              }}
            />
          </div>
        )}

        {/* Folders */}
        {folders.map((folder) => (
          <div key={folder.id}>
            <div
              className={`sidebar-item ${dragOverFolderId === folder.id ? 'active' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOverFolderId(folder.id); }}
              onDragLeave={() => setDragOverFolderId(null)}
              onDrop={(e) => handleFolderDrop(e, folder.id)}
              onContextMenu={(e) => { e.preventDefault(); setFolderCtxMenu({ id: folder.id, x: e.clientX, y: e.clientY }); }}
              onClick={() => toggleFolder(folder.id)}
            >
              <span className="sidebar-item-icon" style={{ display: 'flex', alignItems: 'center' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg></span>
              {editingFolderId === folder.id ? (
                <input
                  autoFocus
                  value={editFolderName}
                  onChange={(e) => setEditFolderName(e.target.value)}
                  onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') handleRenameFolder(folder.id); if (e.key === 'Escape') setEditingFolderId(null); }}
                  onBlur={() => handleRenameFolder(folder.id)}
                  onClick={(e) => e.stopPropagation()}
                  style={{ background: 'white', border: '1px solid #d0d4e4', color: '#323338', outline: 'none', borderRadius: 4, padding: '2px 6px', fontSize: 13, width: '100%' }}
                />
              ) : (
                <span>{folder.name}</span>
              )}
            </div>
            {expandedFolders.has(folder.id) && (
              <div style={{ paddingLeft: 16 }}>
                {boardsInFolder(folder.id).map(renderBoardItem)}
                {renderAddBoard(folder.id)}
              </div>
            )}
          </div>
        ))}

        {/* Root boards */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOverFolderId('__root__'); }}
          onDragLeave={() => setDragOverFolderId(null)}
          onDrop={(e) => handleFolderDrop(e, null)}
          style={{ borderTop: dragOverFolderId === '__root__' ? '2px solid #6161ff' : undefined }}
        >
          {rootBoards.map(renderBoardItem)}
          {renderAddBoard(null)}
        </div>

        {/* Archived section */}
        {archivedBoards.length > 0 && (
          <>
            <div
              className="sidebar-section-title"
              style={{ cursor: 'pointer', marginTop: 8 }}
              onClick={() => setShowArchived(!showArchived)}
            >
              {showArchived ? '▼' : '▶'} Archived ({archivedBoards.length})
            </div>
            {showArchived && archivedBoards.map((board) => (
              <div
                key={board.id}
                className="sidebar-item"
                style={{ opacity: 0.6 }}
                onContextMenu={(e) => { e.preventDefault(); setBoardCtxMenu({ board, x: e.clientX, y: e.clientY }); }}
              >
                <span className="sidebar-item-icon" style={{ display: 'flex', alignItems: 'center' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 8v13H3V8"/><path d="M1 3h22v5H1z"/><path d="M10 12h4"/></svg></span>
                <span>{board.name}</span>
              </div>
            ))}
          </>
        )}
      </div>

      {/* ── Board Context Menu ── */}
      {boardCtxMenu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 9997 }} onClick={() => { setBoardCtxMenu(null); setMoveToMenu(null); }} />
          <div className="context-menu" style={{ position: 'fixed', left: boardCtxMenu.x, top: boardCtxMenu.y, zIndex: 9998 }}>
            <button className="context-menu-item" onClick={() => handleOpenNewTab(boardCtxMenu.board)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg> Open in new tab
            </button>
            <button className="context-menu-item" onClick={() => { setShareBoardId(boardCtxMenu.board.id); setBoardCtxMenu(null); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg> Share
            </button>
            <div className="context-menu-divider" />
            <button className="context-menu-item" onClick={() => handleRenameBoard(boardCtxMenu.board)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg> Rename
            </button>
            <button className="context-menu-item" onClick={() => setMoveToMenu(boardCtxMenu.board.id)} style={{ position: 'relative' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M5 12h14"/><polyline points="12 5 19 12 12 19"/></svg> Move to
              {moveToMenu === boardCtxMenu.board.id && (
                <div className="context-menu" style={{ position: 'absolute', left: '100%', top: 0, minWidth: 160 }}>
                  <button className="context-menu-item" onClick={() => handleMoveBoard(boardCtxMenu.board.id, null)}>
                    Root (no folder)
                  </button>
                  {folders.map((f) => (
                    <button key={f.id} className="context-menu-item" onClick={() => handleMoveBoard(boardCtxMenu.board.id, f.id)}>
                      {f.name}
                    </button>
                  ))}
                </div>
              )}
            </button>
            <button className="context-menu-item" onClick={async () => {
              const val = boardCtxMenu.board.is_private ? 0 : 1;
              await api.updateBoard(boardCtxMenu.board.id, { is_private: val } as any);
              dispatch({ type: 'UPDATE_BOARD', id: boardCtxMenu.board.id, data: { is_private: val } });
              setBoardCtxMenu(null);
            }}>
              {boardCtxMenu.board.is_private
                ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              } {boardCtxMenu.board.is_private ? 'Make shareable' : 'Make private'}
            </button>
            <button className="context-menu-item" onClick={() => handleToggleFavorite(boardCtxMenu.board)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill={boardCtxMenu.board.is_favorite ? '#f5a623' : 'none'} stroke={boardCtxMenu.board.is_favorite ? '#f5a623' : 'currentColor'} strokeWidth="1.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg> {boardCtxMenu.board.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
            </button>
            <button className="context-menu-item" onClick={() => handleDuplicateBoard(boardCtxMenu.board)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg> Duplicate
            </button>
            <div className="context-menu-divider" />
            <button className="context-menu-item danger" onClick={() => handleDeleteBoard(boardCtxMenu.board)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg> Delete
            </button>
            <button className="context-menu-item" onClick={() => handleArchiveBoard(boardCtxMenu.board)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 8v13H3V8"/><path d="M1 3h22v5H1z"/><path d="M10 12h4"/></svg> {boardCtxMenu.board.is_archived ? 'Unarchive' : 'Archive'}
            </button>
          </div>
        </>
      )}

      {/* ── Folder Context Menu ── */}
      {folderCtxMenu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 9997 }} onClick={() => setFolderCtxMenu(null)} />
          <div className="context-menu" style={{ position: 'fixed', left: folderCtxMenu.x, top: folderCtxMenu.y, zIndex: 9998 }}>
            <button className="context-menu-item" onClick={() => { const f = state.folders.find((f) => f.id === folderCtxMenu.id); setEditFolderName(f?.name || ''); setEditingFolderId(folderCtxMenu.id); setFolderCtxMenu(null); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg> Rename
            </button>
            <button className="context-menu-item" onClick={() => { setAddingBoard(true); setAddingBoardFolderId(folderCtxMenu.id); setFolderCtxMenu(null); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> Add board here
            </button>
            <div className="context-menu-divider" />
            <button className="context-menu-item danger" onClick={() => { handleDeleteFolder(folderCtxMenu.id); setFolderCtxMenu(null); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg> Delete folder
            </button>
          </div>
        </>
      )}

      <div className="sidebar-bottom">
        {state.user?.org_role === 'admin' ? (
          showInvite ? (
            <div>
              <div style={{ fontSize: 13, marginBottom: 8 }}>Invite to workspace</div>
              <div className="invite-form">
                <input
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="email@example.com"
                  onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
                  style={{ background: 'rgba(255,255,255,0.08)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}
                />
                <button onClick={handleInvite} style={{ padding: '6px 12px', fontSize: 12 }}>Send</button>
              </div>
              {inviteMsg && <div style={{ fontSize: 12, marginTop: 4, color: '#a9b0ff' }}>{inviteMsg}</div>}
              <button onClick={() => setShowInvite(false)} style={{ fontSize: 12, color: '#676879', marginTop: 4 }}>Cancel</button>
            </div>
          ) : (
            <button className="sidebar-invite-btn" onClick={() => setShowInvite(true)}>+ Invite to Workspace</button>
          )
        ) : (
          <div style={{ fontSize: 12, color: '#676879', textAlign: 'center' }}>
            Only admins can invite members
          </div>
        )}
      </div>
      {shareBoardId && (
        <ShareBoard boardId={shareBoardId} onClose={() => setShareBoardId(null)} />
      )}
    </div>
  );
}
